/**
 * Per-child adaptive difficulty driven by rolling quiz accuracy.
 *
 * - We track the kid's last 8 quiz percentages and a running mean.
 * - Once 5+ recent scores exist, we evaluate after each quiz.
 * - Adjustment is sticky for 5 quizzes after any change so the band can't
 *   oscillate back-and-forth on a single off-day.
 *
 * - rolling >= 85% → bump difficulty +1 (cap +1)
 * - rolling <= 50% → bump difficulty −1 (floor −1)
 *
 * Pure helpers — no React, no store dependencies. Trivially testable.
 */

import type { AgeGroup } from '../types/child';

export type DifficultyAdjustment = -1 | 0 | 1;
export type DifficultyBand = 'easy' | 'medium' | 'hard';

export interface AdaptiveState {
  /** Mean of `recentScores`, rounded to whole percent. */
  rollingAccuracy: number;
  difficultyAdjustment: DifficultyAdjustment;
  /** Last N quiz percentages (FIFO, max 8). */
  recentScores: number[];
  /** How many quizzes have been recorded since the last adjustment change.
   *  Used to enforce the cooldown. */
  scoresSinceAdjustment: number;
  /** YYYY-MM-DD of the last adjustment change (informational). */
  lastAdjustedAt: string;
  /** Per-dimension competency: keys are `${dimension}:${value}`, e.g.
   *  `region:europe` or `qtype:capital`. Used for targeted-practice quiz
   *  generation — weak buckets get surfaced more often. */
  competency?: Record<string, CompetencyEntry>;
}

export interface CompetencyEntry {
  attempts: number;
  correct: number;
  /** ISO timestamp of the last attempt — used to age-out stale buckets. */
  lastAt: string;
}

/** Build the canonical key used by competency entries. */
export function competencyKey(dimension: string, value: string): string {
  return `${dimension}:${value}`;
}

const WINDOW_SIZE = 8;
const MIN_SCORES_FOR_ADJUSTMENT = 5;
const COOLDOWN_QUIZZES = 5;
const UPSHIFT_THRESHOLD = 85;
const DOWNSHIFT_THRESHOLD = 50;

const AGE_TO_BAND: Record<AgeGroup, DifficultyBand> = {
  'pre-k': 'easy',
  'kinder': 'easy',
  'early-elementary': 'medium',
  'upper-elementary': 'hard',
};

const BAND_ORDER: DifficultyBand[] = ['easy', 'medium', 'hard'];

function todayStr(): string {
  return new Date().toISOString().split('T')[0];
}

/** Push a fresh quiz percentage into the adaptive state and re-evaluate. */
export function pushQuizScore(
  prev: AdaptiveState | undefined,
  percent: number,
): AdaptiveState {
  const today = todayStr();
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));
  const recent = [...(prev?.recentScores ?? []), clamped].slice(-WINDOW_SIZE);
  const rolling = recent.reduce((a, b) => a + b, 0) / recent.length;
  const currentAdjustment: DifficultyAdjustment = prev?.difficultyAdjustment ?? 0;
  const scoresSince = (prev?.scoresSinceAdjustment ?? 0) + 1;

  const enoughHistory = recent.length >= MIN_SCORES_FOR_ADJUSTMENT;
  const cooldownPassed = currentAdjustment === 0 || scoresSince >= COOLDOWN_QUIZZES;

  let nextAdjustment = currentAdjustment;
  let nextScoresSince = scoresSince;
  let lastAdjustedAt = prev?.lastAdjustedAt ?? today;

  if (enoughHistory && cooldownPassed) {
    if (rolling >= UPSHIFT_THRESHOLD && currentAdjustment < 1) {
      nextAdjustment = (currentAdjustment + 1) as DifficultyAdjustment;
      nextScoresSince = 0;
      lastAdjustedAt = today;
    } else if (rolling <= DOWNSHIFT_THRESHOLD && currentAdjustment > -1) {
      nextAdjustment = (currentAdjustment - 1) as DifficultyAdjustment;
      nextScoresSince = 0;
      lastAdjustedAt = today;
    }
  }

  return {
    rollingAccuracy: Math.round(rolling),
    difficultyAdjustment: nextAdjustment,
    recentScores: recent,
    scoresSinceAdjustment: nextScoresSince,
    lastAdjustedAt,
  };
}

/** Returns the difficulty band the kid should actually experience. */
export function getEffectiveBand(
  ageGroup: AgeGroup,
  adjustment: DifficultyAdjustment = 0,
): DifficultyBand {
  const baseIndex = BAND_ORDER.indexOf(AGE_TO_BAND[ageGroup]);
  const shifted = Math.max(0, Math.min(BAND_ORDER.length - 1, baseIndex + adjustment));
  return BAND_ORDER[shifted];
}

/** Numeric difficulty (1–3) derived from the band — used for arcade-bonus calc. */
export function bandToDiffNum(band: DifficultyBand): 1 | 2 | 3 {
  return band === 'hard' ? 3 : band === 'medium' ? 2 : 1;
}

/** Whether the band shifted up vs. the kid's age-default — used to gate the celebratory banner. */
export function didShiftUp(ageGroup: AgeGroup, adjustment: DifficultyAdjustment): boolean {
  return adjustment > 0;
}

/** Whether the band shifted down — used to show a gentler banner. */
export function didShiftDown(ageGroup: AgeGroup, adjustment: DifficultyAdjustment): boolean {
  return adjustment < 0;
}

/** Push a single per-question attempt across one or more dimensions. Returns
 *  a fresh AdaptiveState with the per-dimension counters incremented. The
 *  rolling accuracy / difficulty adjustment fields are NOT touched here —
 *  call `pushQuizScore` once at quiz completion for that. */
export function recordCompetencyAttempt(
  prev: AdaptiveState | undefined,
  dimensions: Array<{ dimension: string; value: string }>,
  correct: boolean,
): AdaptiveState {
  const now = new Date().toISOString();
  const competency: Record<string, CompetencyEntry> = { ...(prev?.competency ?? {}) };
  for (const { dimension, value } of dimensions) {
    if (!value) continue;
    const key = competencyKey(dimension, value);
    const existing = competency[key];
    competency[key] = {
      attempts: (existing?.attempts ?? 0) + 1,
      correct: (existing?.correct ?? 0) + (correct ? 1 : 0),
      lastAt: now,
    };
  }
  return {
    rollingAccuracy: prev?.rollingAccuracy ?? 0,
    difficultyAdjustment: prev?.difficultyAdjustment ?? 0,
    recentScores: prev?.recentScores ?? [],
    scoresSinceAdjustment: prev?.scoresSinceAdjustment ?? 0,
    lastAdjustedAt: prev?.lastAdjustedAt ?? todayStr(),
    competency,
  };
}

/** Read the % accuracy for a given dimension value. Returns null when no
 *  attempts have been logged. */
export function getDimensionAccuracy(
  state: AdaptiveState | undefined,
  dimension: string,
  value: string,
): number | null {
  const entry = state?.competency?.[competencyKey(dimension, value)];
  if (!entry || entry.attempts === 0) return null;
  return Math.round((entry.correct / entry.attempts) * 100);
}

/** Find the N weakest values within a dimension that have at least
 *  `minAttempts` attempts logged. Returns values sorted lowest-accuracy first.
 *  When fewer than N qualify, returns however many do. */
export function getWeakestDimensionValues(
  state: AdaptiveState | undefined,
  dimension: string,
  n: number,
  minAttempts = 3,
): { value: string; accuracy: number; attempts: number }[] {
  if (!state?.competency) return [];
  const prefix = `${dimension}:`;
  const candidates: { value: string; accuracy: number; attempts: number }[] = [];
  for (const [key, entry] of Object.entries(state.competency)) {
    if (!key.startsWith(prefix)) continue;
    if (entry.attempts < minAttempts) continue;
    const accuracy = (entry.correct / entry.attempts) * 100;
    candidates.push({ value: key.slice(prefix.length), accuracy, attempts: entry.attempts });
  }
  candidates.sort((a, b) => a.accuracy - b.accuracy);
  return candidates.slice(0, n);
}
